import React, { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { isReviewDueSoon } from '~clients/config/carePlanEnums';
import { getCarePlanTypeConfig } from '~clients/config/carePlanTypes';

import { formatDateString } from '~libs/dayjs';

import { SectionCard } from '~components';
import { Fields } from '~components/form-fields';

import { overviewPlanDetailsFields } from '~clients/forms/care-plans/overviewPlanDetails';

import { getAll as getUsers } from '~care-workers/actions';

import { selectCurrentClient } from '~main/selectors/clients/clients';

import { OverviewSectionProps } from './overviewSectionProps';

/**
 * Plan details — read-only rows (type, MAC/NDIS number, review due date) plus editable
 * fields (start/end date, care partner), rendered into the Overview tab's shared form.
 */
const PlanDetailsSection: React.FC<OverviewSectionProps> = ({
  plan,
  values,
  setFieldValue,
  setFieldTouched,
  touched,
  invalidFields,
}) => {
  const dispatch = useAppDispatch();
  const client = useAppSelector(selectCurrentClient);

  useEffect(() => {
    dispatch(getUsers());
  }, [dispatch]);

  // The clients slice row is shared state — guard against a stale client from another page.
  const isCurrentClient = client?.id === plan.client_id;
  const identityNumber = (isCurrentClient && client?.ndis_number) || '—';

  const definitions = useMemo(() => {
    const fields = overviewPlanDetailsFields({ isReviewDueSoon: isReviewDueSoon(plan.review_due_date) });
    return Object.entries(fields).map(([key, props]) => ({ key, ...props }));
  }, [plan.review_due_date]);

  // Read-only display values (not in the form) merged with the shared editable values.
  const fieldValues = {
    plan_type_label: getCarePlanTypeConfig(plan.plan_type).label,
    identity_number: identityNumber,
    // Matches the MAC/NDIS row's empty placeholder — a plan has no review date until activation.
    review_due_date: formatDateString(plan.review_due_date) || '—',
    start_date: values.start_date,
    end_date: values.end_date,
    carer_id: values.carer_id,
  };

  return (
    <SectionCard title="Plan Details" titleTag="h4" className="care-plan-overview-section" dataCy="care-plan-details">
      <Fields
        definitions={definitions}
        values={fieldValues}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        touched={touched}
        invalidFields={invalidFields}
      />
    </SectionCard>
  );
};

export default PlanDetailsSection;
